// EchoCodesManager.js - Main container for echo codes management (simplified)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import EchoCodeSearch from './EchoCodeSearch';
import EchoCodesList from './EchoCodesList';
import EchoCodesMacroButton from './EchoCodesMacroButton';
import EchoCodesWebExportButton from './EchoCodesWebExportButton';
import {
  extractEchoCodes,
  mergeCodesWithExisting,
  addCodeManually,
  removeCode,
  updateCodeModifier,
  updateCodeValue,
  getTotalCodeCount
} from './parseEchoCodes';
import { getCategoryForCode } from './echo-codes/echoCodesLoader';
import './EchoCodesManager.css';

/**
 * Main manager component for echo codes
 * Handles auto-extraction, manual addition, and display of echo codes
 */
const EchoCodesManager = ({
  reportText = '',
  structuredData = {},
  onChange,
  readOnly = false
}) => {
  const [codes, setCodes] = useState(() => {
    // Initialize from new format: llmCodes + autoCodes
    const llmCodes = structuredData?.llmCodes || [];
    const autoCodes = structuredData?.autoCodes || [];

    // Merge both arrays
    const allCodes = [...llmCodes, ...autoCodes];

    // Create empty category structure
    const grouped = {
      vg: [],
      vd: [],
      oreillettes: [],
      valve_mitrale: [],
      valve_tricuspide: [],
      valve_aortique: [],
      valve_pulmonaire: [],
      gros_vaisseaux: [],
      pericarde: [],
      divers: []
    };

    // Group codes by category based on their prefix
    allCodes.forEach(codeObj => {
      const category = getCategoryForCode(codeObj.code);
      if (category && grouped[category]) {
        grouped[category].push({
          code: codeObj.code,
          // Normalize modifier to lowercase for consistency with enum values
          modifier: codeObj.modifier ? codeObj.modifier.toLowerCase() : null,
          value: codeObj.value !== undefined ? codeObj.value : null,
          source: 'loaded' // Mark as loaded from backend
        });
      }
    });

    return grouped;
  });

  const [notification, setNotification] = useState(null);
  const extractTimerRef = useRef(null);
  const previousReportTextRef = useRef(reportText);

  /**
   * Convert category-grouped codes back to new format (llmCodes + autoCodes)
   * - Codes with source='auto' or with values go to autoCodes
   * - Manually added codes go to llmCodes
   */
  const convertToNewFormat = useCallback((categorizedCodes) => {
    const llmCodes = [];
    const autoCodes = [];

    Object.keys(categorizedCodes).forEach(category => {
      const categoryArray = categorizedCodes[category] || [];

      categoryArray.forEach(codeObj => {
        const codeData = {
          code: codeObj.code
        };

        // Add optional fields
        if (codeObj.modifier) {
          codeData.modifier = codeObj.modifier;
        }
        if (codeObj.value !== null && codeObj.value !== undefined) {
          codeData.value = codeObj.value;
        }

        // Decide which array based on source or presence of value
        if (codeObj.source === 'auto' || codeObj.value !== null) {
          autoCodes.push(codeData);
        } else {
          llmCodes.push(codeData);
        }
      });
    });

    return { llmCodes, autoCodes };
  }, []);

  // Auto-extract codes when reportText changes (debounced)
  useEffect(() => {
    // Only extract if text actually changed
    if (reportText === previousReportTextRef.current) {
      return;
    }

    previousReportTextRef.current = reportText;

    // Clear previous timer
    if (extractTimerRef.current) {
      clearTimeout(extractTimerRef.current);
    }

    // Debounce extraction by 500ms
    extractTimerRef.current = setTimeout(() => {
      try {
        // Extract codes from text
        const autoCodes = extractEchoCodes(reportText);

        // Merge with existing codes (preserves manual codes)
        const merged = mergeCodesWithExisting(autoCodes, codes);

        setCodes(merged);

        // Notify parent with new format
        if (onChange) {
          const { llmCodes, autoCodes } = convertToNewFormat(merged);
          onChange({
            ...structuredData,
            llmCodes,
            autoCodes
          });
        }
      } catch (error) {
        console.error('Error extracting codes:', error);
      }
    }, 500);

    // Cleanup
    return () => {
      if (extractTimerRef.current) {
        clearTimeout(extractTimerRef.current);
      }
    };
  }, [reportText, codes, structuredData, onChange, convertToNewFormat]);

  // Show notification helper
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 2500);
  }, []);

  // Handle adding a code manually
  const handleAddCode = useCallback((code, modifier) => {
    const result = addCodeManually(codes, code, modifier);

    if (result.success) {
      setCodes(result.codes);

      // Notify parent with new format
      if (onChange) {
        const { llmCodes, autoCodes } = convertToNewFormat(result.codes);
        onChange({
          ...structuredData,
          llmCodes,
          autoCodes
        });
      }
    } else {
      showNotification(result.message, 'error');
    }
  }, [codes, structuredData, onChange, showNotification, convertToNewFormat]);

  // Handle removing a code (simplified - no confirmation)
  const handleRemoveCode = useCallback((category, code) => {
    const result = removeCode(codes, category, code, true); // Always confirmed

    if (result.success) {
      setCodes(result.codes);
      showNotification('Code supprimé', 'success');

      // Notify parent with new format
      if (onChange) {
        const { llmCodes, autoCodes } = convertToNewFormat(result.codes);
        onChange({
          ...structuredData,
          llmCodes,
          autoCodes
        });
      }
    }
  }, [codes, structuredData, onChange, showNotification, convertToNewFormat]);

  // Handle updating a code's modifier
  const handleModifierChange = useCallback((category, code, modifier) => {
    const newCodes = updateCodeModifier(codes, category, code, modifier);
    setCodes(newCodes);

    // Notify parent with new format
    if (onChange) {
      const { llmCodes, autoCodes } = convertToNewFormat(newCodes);
      onChange({
        ...structuredData,
        llmCodes,
        autoCodes
      });
    }
  }, [codes, structuredData, onChange, convertToNewFormat]);

  // Handle updating a code's value (for numeric codes)
  const handleValueChange = useCallback((category, code, value) => {
    const newCodes = updateCodeValue(codes, category, code, value);
    setCodes(newCodes);

    // Notify parent with new format
    if (onChange) {
      const { llmCodes, autoCodes } = convertToNewFormat(newCodes);
      onChange({
        ...structuredData,
        llmCodes,
        autoCodes
      });
    }
  }, [codes, structuredData, onChange, convertToNewFormat]);

  // Handle reordering codes within a category
  const handleReorder = useCallback((category, reorderedCodes) => {
    const newCodes = {
      ...codes,
      [category]: reorderedCodes
    };
    setCodes(newCodes);

    // Notify parent with new format
    if (onChange) {
      const { llmCodes, autoCodes } = convertToNewFormat(newCodes);
      onChange({
        ...structuredData,
        llmCodes,
        autoCodes
      });
    }
  }, [codes, structuredData, onChange, convertToNewFormat]);

  return (
    <div className="echo-codes-manager">


      {!readOnly && (
        <div className="echo-search-bar-sticky">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <EchoCodeSearch
                onAddCode={handleAddCode}
                existingCodes={codes}
              />
            </div>
            <EchoCodesMacroButton codes={codes} />
            <EchoCodesWebExportButton codes={codes} />
          </div>
        </div>
      )}

      <EchoCodesList
        codes={codes}
        onModifierChange={readOnly ? null : handleModifierChange}
        onValueChange={readOnly ? null : handleValueChange}
        onRemove={readOnly ? null : handleRemoveCode}
        onReorder={readOnly ? null : handleReorder}
      />
    </div>
  );
};

export default EchoCodesManager;
